/**
 * Boot splash — "Powered by SuprAI × Frac".
 *
 * A 1.6-second CSS-3D title card: the two marks fly in from depth, meet at the
 * centre and the whole card lifts away. Shown once per browser session (it is a
 * greeting, not a toll booth), skippable on click or key, and reduced to a plain
 * cross-fade when the visitor asks for reduced motion. No WebGL — the scene
 * budget in three/sceneSlots.js is untouched.
 *
 * The timers are set once on mount and are deliberately NOT keyed to the phase:
 * re-running the effect on every phase change would clear the very timeout that
 * unmounts the card, leaving an invisible overlay holding the scroll lock.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useContent } from '../i18n/LocaleProvider';
import { usePrefersReducedMotion } from '../lib/hooks';
import { FRAC_URL } from './PoweredBy';

const SESSION_KEY = 'usp.boot-splash-v1';
const HOLD = 2780;
const FADE = 320;

const seen = () => {
  try {
    return Boolean(window.sessionStorage.getItem(SESSION_KEY));
  } catch {
    return false;
  }
};

const unlock = () => document.documentElement.classList.remove('is-booting');

export default function BootSplash() {
  const { UI } = useContent();
  const reduced = usePrefersReducedMotion();
  const [phase, setPhase] = useState(() => (seen() ? 'done' : 'play'));
  const reducedRef = useRef(reduced);
  reducedRef.current = reduced;

  useEffect(() => {
    if (phase === 'done') return undefined;
    try {
      window.sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      /* storage blocked — the card simply plays again next load */
    }
    document.documentElement.classList.add('is-booting');
    const hold = reducedRef.current ? 2020 : HOLD;
    const out = window.setTimeout(() => setPhase('out'), hold);
    const end = window.setTimeout(() => {
      unlock();
      setPhase('done');
    }, hold + FADE);
    return () => {
      window.clearTimeout(out);
      window.clearTimeout(end);
      unlock();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (phase === 'done') return null;

  const skip = () => {
    unlock();
    setPhase('done');
  };

  return (
    <div
      className={`boot-splash ${phase === 'out' ? 'boot-splash--out' : ''} ${
        reduced ? 'boot-splash--still' : ''
      }`}
      role="img"
      aria-label={`${UI.common.poweredBy} SuprAI × Frac`}
      onClick={skip}
      data-testid="boot-splash"
    >
      <div className="boot-splash__stage" aria-hidden="true">
        <p className="boot-splash__label">{UI.common.poweredBy}</p>
        <div className="boot-splash__row">
          <span className="boot-splash__mark boot-splash__mark--left">
            <img src="/brand/suprai-logo-full.webp" alt="" className="boot-splash__suprai" />
          </span>
          <span className="boot-splash__x">×</span>
          <span className="boot-splash__mark boot-splash__mark--right">
            <img src="/brand/frac-logo.png" alt="" className="boot-splash__frac" />
          </span>
        </div>
        <span className="boot-splash__rule" />
      </div>
      <button
        type="button"
        className="boot-splash__skip"
        onClick={skip}
        data-testid="boot-splash-skip"
      >
        {UI.common.skipIntro}
      </button>
      <a className="u-sr-only" href={FRAC_URL} target="_blank" rel="noopener noreferrer">
        Frac
      </a>
    </div>
  );
}
